import axios from 'axios'
import { installLicenseRetry } from './licenseRetryHttp'
import { getRecaptchaToken } from '@/utils/recaptcha'
import { notifyLicenseRetryExhausted } from './licenseRetryNotice'
import router from '@/router'
import { getAuth, clearAuth } from '../utils/authStorage'

const MAX_UNAUTHORIZED_RETRIES = 1
let unauthorizedStreak = 0

const api = axios.create({
  baseURL: '',
  timeout: 60000,
})

installLicenseRetry(api, {
  getContext: () => ({ token: getAuth()?.token, route: router.currentRoute.value.fullPath }),
  refreshLoginCaptcha: () => getRecaptchaToken('login'),
  onExhausted: notifyLicenseRetryExhausted,
})

// Request interceptor - add token
api.interceptors.request.use(config => {
  const token = getAuth()?.token
  if (token && !config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`
  return config
})

// Response interceptor - logout on consecutive 401s
api.interceptors.response.use(
  (response) => {
    unauthorizedStreak = 0
    return response
  },
  (error) => {
    const isLoginRequest = error.config?.url === '/api/user/login'
    const isLogoutRequest = error.config?.url === '/api/user/logout'

    if (error.response?.status === 401) {
      // Invalid login credentials should be handled by the login form itself.
      // Redirecting here makes the login page look like it refreshed and hides the error.
      if (!isLoginRequest && !isLogoutRequest) {
        unauthorizedStreak += 1
        if (unauthorizedStreak >= MAX_UNAUTHORIZED_RETRIES) {
          clearAuth()
          unauthorizedStreak = 0
          router.push('/')
        }
      }
    }
    if (error.response?.status && error.response.status !== 401) {
      unauthorizedStreak = 0
    }
    return Promise.reject(error)
  }
)

export const apiService = {
  async checkHealth() {
    const response = await api.get('/api/check')
    return response.data
  },

  // Models
  async getModels() {
    const response = await api.get('/api/models')
    return response.data
  },

  // Token Pricing API methods (Admin)
  async getAllTokenPricing() {
    const response = await api.get('/api/admin/token-pricing')
    return response.data
  },

  async createOrUpdateTokenPricing(data: {
    modelType: string
    inputTokenPrice: number
    outputTokenPrice: number
    cacheReadInputTokenPrice?: number | null
    cacheCreationInputTokenPrice?: number | null
  }) {
    const response = await api.post('/api/admin/token-pricing', data)
    return response.data
  },

  // Package Limits API methods (Admin)
  async getPackageLimits() {
    const response = await api.get('/api/admin/package-limits')
    return response.data
  },

  async updatePackageLimit(pkg: string, monthlyLimit: number) {
    const response = await api.put(`/api/admin/package-limits/${pkg}`, { monthlyLimit })
    return response.data
  },
}

export default api
